// @ts-strict-ignore
import React from 'react'
import { startCase } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { Box, DataListItem, RowsSkeletonLoader } from '@node-space/ui-components'
import { ellipseMiddleText } from '@node-space/utils'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useCountriesQuery } from 'hooks/queries/useCountriesQuery'
import { Country } from 'types/authentication/Profile'
import {
  BeneficiaryDetails,
  BeneficiaryEntityType,
  BeneficiaryTransferDestination,
  USDBeneficiaryOptionsLabelMapping,
} from 'types/beneficiaries'
import { getAccountNumberAndBankCodeLabelKeys, getAddress } from 'utils/beneficiaries'
import { sentenceCase } from 'utils/utils'

export type BeneficiarySidePanelBodyProps = {
  beneficiaryDetails: BeneficiaryDetails
  getCountryNameByCode: (code: string, countries: Country[]) => string
}

export const BeneficiarySidePanelBody = ({
  beneficiaryDetails,
  getCountryNameByCode,
}: BeneficiarySidePanelBodyProps) => {
  const { t } = useTranslation()
  const { data: countries, isPending: isLoadingCountries } = useCountriesQuery(true)

  // Beneficiary Details
  const {
    accountNumber,
    accountType,
    bankName,
    code: bankCode,
    nid,
  } = beneficiaryDetails?.fiat || {}
  const paymentMethod = beneficiaryDetails?.paymentMethod
  const dateOfBirth = beneficiaryDetails?.dateOfBirth
  const { country: beneficiaryAddressCountry } = beneficiaryDetails?.address || {}
  const beneficiaryAddress = getAddress(
    beneficiaryDetails?.address,
    getCountryNameByCode(beneficiaryDetails?.address?.country, countries)
  )
  const isPersonalDataPresent = !!dateOfBirth || !!beneficiaryAddress
  const isInternationalBeneficiary =
    beneficiaryDetails?.transferDestination === BeneficiaryTransferDestination.INTERNATIONAL

  const accountNumberAndBankCodeLabelKeys = getAccountNumberAndBankCodeLabelKeys(
    beneficiaryDetails?.currency,
    beneficiaryDetails?.transferDestination
  )

  // Bank Details
  const { country: bankAddressCountry } = beneficiaryDetails?.fiat?.bankAddress || {}

  // Intermediary Bank Details
  const {
    bankAddress: intermediaryBankAddress,
    code: intermediaryBankBankCode,
    bankName: intermediaryBankName,
    nid: intermediaryNid,
  } = beneficiaryDetails?.fiat?.intermediaryBank || {}
  const {
    addressLine1: intermediaryBankAddressLine1,
    city: intermediaryBankCity,
    postCode: intermediaryBankPostCode,
    region: intermediaryBankRegion,
    country: intermediaryBankCountry,
  } = intermediaryBankAddress || {}
  const hasIntermediaryBankAddress =
    intermediaryBankAddressLine1 || intermediaryBankCity || intermediaryBankCountry
  const isIntermediaryBankDataPresent = intermediaryBankBankCode || intermediaryBankName

  const bankAddress = getAddress(
    beneficiaryDetails?.fiat?.bankAddress,
    getCountryNameByCode(bankAddressCountry, countries)
  )

  return isLoadingCountries ? (
    <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />
  ) : (
    <Box>
      <h4 className="text-foreground-prominent text-h4">{t('bankDetails')}</h4>
      <Box paddingT={8} paddingB={12}>
        {accountNumber && (
          <DataListItem
            id="account-number"
            testId="account-number"
            title={t(accountNumberAndBankCodeLabelKeys?.accountNumberLabelKey)}
            body={ellipseMiddleText(accountNumber, 8, 8)}
            copyText={accountNumber}
          />
        )}
        {bankCode && (
          <DataListItem
            id="bank-code"
            testId="bank-code"
            title={t(accountNumberAndBankCodeLabelKeys?.bankCodeLabelKey)}
            body={ellipseMiddleText(bankCode, 20, 10)}
            copyText={bankCode}
          />
        )}
        {nid && (
          <DataListItem
            id="national-bank-code"
            testId="national-bank-code"
            title={t(bankAddressCountry === 'US' ? 'abaRoutingNumber' : 'nationalBankCode')}
            body={ellipseMiddleText(nid, 20, 10)}
            copyText={nid}
          />
        )}
        {accountType && (
          <DataListItem
            id="account-type"
            testId="account-type"
            title={t('accountType')}
            body={sentenceCase(accountType?.toLowerCase())}
          />
        )}
        {paymentMethod && (
          <DataListItem
            id="payment-method"
            testId="payment-method"
            title={t('paymentMethod')}
            body={USDBeneficiaryOptionsLabelMapping[paymentMethod]}
          />
        )}
        {bankName && (
          <DataListItem
            id="bank-name"
            testId="bank-name"
            title={t('bankName')}
            body={startCase(bankName?.toLowerCase())}
          />
        )}
        {!!bankAddress && (
          <DataListItem
            id="bank-address"
            testId="bank-address"
            title={t('bankAddress')}
            body={bankAddress}
          />
        )}
      </Box>
      {isIntermediaryBankDataPresent && isInternationalBeneficiary && (
        <Box>
          <h4 className="text-foreground-prominent text-h4">
            {t('manageBeneficiaries.intermediaryBankDetails')}
          </h4>
          <Box paddingT={8} paddingB={12}>
            {intermediaryBankBankCode && (
              <DataListItem
                id="intermediary-bank-code"
                testId="intermediary-bank-code"
                title={t(accountNumberAndBankCodeLabelKeys?.bankCodeLabelKey)}
                body={ellipseMiddleText(intermediaryBankBankCode, 20, 10)}
                copyText={intermediaryBankBankCode}
              />
            )}
            {intermediaryNid && (
              <DataListItem
                id="intermediary-national-bank-code"
                testId="intermediary-national-bank-code"
                title={t(bankAddressCountry === 'US' ? 'abaRoutingNumber' : 'nationalBankCode')}
                body={ellipseMiddleText(intermediaryNid, 20, 10)}
                copyText={intermediaryNid}
              />
            )}
            {intermediaryBankName && (
              <DataListItem
                id="intermediary-bank-name"
                testId="intermediary-bank-name"
                title={t('bankName')}
                body={intermediaryBankName}
              />
            )}
            {hasIntermediaryBankAddress && (
              <DataListItem
                id="intermediary-bank-address"
                testId="intermediary-bank-address"
                title={t('bankAddress')}
                body={`${intermediaryBankAddressLine1}, ${intermediaryBankCity}, ${intermediaryBankPostCode}, ${intermediaryBankRegion}, ${getCountryNameByCode(
                  intermediaryBankCountry,
                  countries
                )}`}
              />
            )}
          </Box>
        </Box>
      )}
      {isPersonalDataPresent && (
        <Box>
          <h4 className="text-foreground-prominent text-h4">{t('personalDetails')}</h4>
          <Box paddingT={8} paddingB={8}>
            {dateOfBirth && (
              <DataListItem
                id="date-of-birth"
                testId="date-of-birth"
                title={t('formFields.dateOfBirth')}
                body={dateOfBirth}
              />
            )}
            {!!beneficiaryAddress && (
              <DataListItem
                id="beneficiary-address"
                testId="beneficiary-address"
                title={t('manageBeneficiaries.beneficiaryAddress')}
                body={beneficiaryAddress}
              />
            )}
            {!beneficiaryAddress && beneficiaryAddressCountry && (
              <DataListItem
                id="beneficiary-address-country"
                testId="beneficiary-address-country"
                title={
                  beneficiaryDetails?.entityType === BeneficiaryEntityType.BUSINESS
                    ? t('manageBeneficiaries.countryOfRegistration')
                    : t('manageBeneficiaries.countryOfResidence')
                }
                body={getCountryNameByCode(beneficiaryAddressCountry, countries)}
              />
            )}
          </Box>
        </Box>
      )}
    </Box>
  )
}
